import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import { toast, registerToast } from '../MapboxMap/toastService';
import { circleTwoClickMode, markerCircleMode, DrawExtraControls, makeCircleFeature, drawStyles, restoreDragPan } from '../MapboxMap/drawModes';
import DrawnShapesList from '../MapboxMap/DrawnShapesList';
import ShapeAttributesEditor from '../MapboxMap/ShapeAttributesEditor';

import geolocationService from '../../services/geolocationService';
import colors from '../../util/colors';
import { estados } from '../../util/estados';

import FormFiltersFornecedor from './FormFiltersFornecedor';
import SupplyTableModal from './SupplyTableModal';
import '../mapa.css';
import './MapaFornecedorMapbox.css';

const BRAZIL_CENTER = [-51.9253, -14.235];
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 12;
const SOURCE_ID = 'empreendimentos';
const BAIRROS_SOURCE_ID = 'bairros';
const CLUSTER_LAYERS = ['clusters', 'cluster-count', 'unclustered-point'];
const EMPTY_FC = { type: 'FeatureCollection', features: [] };

// Ícones dos marcadores (mesmos nomes devolvidos por EmpreendimentosMapboxController::resolveIcon).
const ICON_LETTERS = ['E', 'O', 'S', 'M', 'A', 'L', 'X', 'H', 'C'];
const ICON_NAMES = [
    ...ICON_LETTERS.flatMap((l) => [`green_Marker${l}`, `red_Marker${l}`]),
    'yellow_MarkerF',
];

// Cidades com limites de bairros (GeoJSON) disponíveis, no formato "UF-Cidade".
const BAIRROS_CITIES = new Set(window.bairrosCities || []);

const MAP_STYLE_STORAGE_KEY = 'geobrain.fornecedor-mapbox.style';
const MAP_STYLES = {
    light: 'mapbox://styles/mapbox/streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11',
};
const readStoredMapStyle = () => {
    try {
        const v = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return v === 'dark' || v === 'light' ? v : 'light';
    } catch {
        return 'light';
    }
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_SCRIPT_ID = 'google-maps-places-script';

function loadGoogleMapsPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
        return new Promise((resolve) => {
            document.getElementById(GOOGLE_SCRIPT_ID).addEventListener('load', resolve, { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pt-BR&region=BR`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const INITIAL_FILTERS = {
    estado: null,
    cidades: [],
    tipoAnalise: null,
    area_maior_que: null,
    area_menor_que: null,
    ticket_maior_que: null,
    ticket_menor_que: null,
    pattern: null,
    bairros: [],
    incorporators: [],
    construction_status: null,
    material: [],
};

/**
 * Mapa de Fornecedores em Mapbox — sucessor do mapa Google
 * (MapaFornecedor/DefaulMap.jsx + SuppliersMap.jsx).
 *
 * Diferença de origem dos dados: o mapa Google carrega os marcadores da coleção
 * MongoDB `empreendimentos` (rota `web.empreendimentos.json.without.meta.data`);
 * aqui os marcadores vêm do endpoint GeoJSON do Mapbox
 * (`api.empreendimentos-fornecedor-mapbox` → EmpreendimentosMapboxController@index),
 * que é 100% MySQL — o MongoDB está em desativação e nenhuma feature nova do Mapbox
 * pode depender dele.
 *
 * O recorte por formas desenhadas é feito no cliente (booleanPointInPolygon), como
 * no mapa Mapbox principal, e não por consulta geoespacial no servidor.
 *
 * A tabela de insumos (TablePerPattern) e os relatórios são reusados do mapa Google —
 * já são MySQL (view vw_building_supplies); ver SupplyTableModal.
 */
class MapaFornecedorMapbox extends Component {
    state = {
        left_side_bar_visible: true,
        active_tab: 'filtros',
        loading: false,
        filters: { ...INITIAL_FILTERS },
        bairrosOptions: [],
        totalEmpreendimentos: 0,
        mapStyle: readStoredMapStyle(),
        showEmpreendimentos: true,
        showBairros: false,
        bairrosAvailable: false,
        drawnShapes: [],
        shapeEditorOpen: false,
        shapeEditing: null,
        showSupplyTable: false,
    };

    containerRef = React.createRef();
    searchInputRef = React.createRef();
    toastRef = React.createRef();
    map = null;
    draw = null;
    drawExtras = null;
    currentGeojson = null;
    allFeatures = [];
    bairrosCache = {};
    _hidingIds = new Set();

    componentDidMount() {
        registerToast(this.toastRef);

        const apiKey = window.mapboxApiKey;
        if (!apiKey || !this.containerRef.current) {
            if (!apiKey) console.error('[MapaFornecedorMapbox] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = apiKey;
        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: MAP_STYLES[this.state.mapStyle],
            center: BRAZIL_CENTER,
            zoom: DEFAULT_ZOOM,
        });

        this.map.on('error', (e) => console.error('[MapaFornecedorMapbox] erro:', e.error));

        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        this.map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: { enableHighAccuracy: true },
                trackUserLocation: true,
            }),
            'top-right'
        );

        this.setupDrawTools();
        this.map.on('load', () => this.initializeLayers());
        this.setupGooglePlaces();
    }

    componentWillUnmount() {
        if (this.map) this.map.remove();
    }

    // ===== Camadas =====

    initializeLayers = async () => {
        await this.loadMarkerImages();
        this.setupBairrosLayers();
        this.setupClusterLayers();

        if (this.currentGeojson) {
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        }
        // A troca de estilo recria fontes/camadas: reaplica o que estiver ativo.
        if (this.state.showBairros) {
            this.renderBairros(this.state.filters.cidades);
        }
        if (!this.state.showEmpreendimentos) {
            this.setEmpreendimentosVisibility(false);
        }
    };

    loadMarkerImages = () =>
        Promise.all(
            ICON_NAMES.map(
                (name) =>
                    new Promise((resolve) => {
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/markers/${name}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            if (!this.map.hasImage(name)) this.map.addImage(name, image);
                            resolve();
                        });
                    })
            )
        );

    setupBairrosLayers = () => {
        if (this.map.getSource(BAIRROS_SOURCE_ID)) return;

        this.map.addSource(BAIRROS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
        this.map.addLayer({
            id: 'bairros-fill',
            type: 'fill',
            source: BAIRROS_SOURCE_ID,
            paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
        });
        this.map.addLayer({
            id: 'bairros-outline',
            type: 'line',
            source: BAIRROS_SOURCE_ID,
            paint: { 'line-color': '#0d6efd', 'line-width': 1.5 },
        });
        this.map.addLayer({
            id: 'bairros-label',
            type: 'symbol',
            source: BAIRROS_SOURCE_ID,
            layout: {
                'text-field': ['get', 'name'],
                'text-size': 11,
                'text-allow-overlap': false,
            },
            paint: { 'text-color': '#0d6efd', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
        });
    };

    setupClusterLayers = () => {
        this.map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: EMPTY_FC,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });

        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 50, '#f1f075', 200, '#f28cb1'],
                'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 30],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });

        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 13,
            },
        });

        this.map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': ['get', 'icon'],
                'icon-size': 0.6,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });

        this.map.on('click', 'clusters', (e) => {
            const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const clusterId = features[0].properties.cluster_id;
            this.map.getSource(SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
            });
        });

        // O mapa de fornecedores não abre ficha de empreendimento: o marcador só
        // identifica o prédio que entra na análise de insumos (paridade com o mapa Google,
        // cujo painel de detalhes nunca foi renderizado nesta página).
        const hoverPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });

        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const f = e.features[0];
            const p = f.properties;

            const cityState = p.city && p.state ? `${p.city}/${p.state}` : (p.city || p.state || '');
            const addressParts = [p.address, p.neighborhood, cityState].filter((x) => x && x.length);
            const addressLine = addressParts.length ? addressParts.join(', ') : 'Endereço não disponível';
            const padrao = p.padrao ? `<br/><small>Padrão: ${p.padrao}</small>` : '';

            hoverPopup
                .setLngLat(f.geometry.coordinates.slice())
                .setHTML(`<strong>${p.name || ''}</strong><br/><small>${addressLine}</small>${padrao}`)
                .addTo(this.map);
        });

        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            hoverPopup.remove();
        });

        this.map.on('mouseenter', 'clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'clusters', () => { this.map.getCanvas().style.cursor = ''; });
    };

    setEmpreendimentosVisibility = (visible) => {
        const v = visible ? 'visible' : 'none';
        CLUSTER_LAYERS.forEach((id) => this.map?.getLayer(id) && this.map.setLayoutProperty(id, 'visibility', v));
    };

    toggleEmpreendimentos = (enabled) => {
        this.setState({ showEmpreendimentos: enabled });
        this.setEmpreendimentosVisibility(enabled);
    };

    setMapStyle = (style) => {
        if (style !== 'light' && style !== 'dark') return;
        this.setState({ mapStyle: style });
        try { localStorage.setItem(MAP_STYLE_STORAGE_KEY, style); } catch { /* localStorage indisponível */ }

        const drawn = this.draw?.getAll();
        this.map.setStyle(MAP_STYLES[style]);
        this.map.once('style.load', () => {
            this.initializeLayers();
            if (drawn?.features?.length) this.draw.set(drawn);
        });
    };

    // ===== Ferramentas de desenho =====

    setupDrawTools = () => {
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            userProperties: true,
            styles: drawStyles,
            modes: {
                ...MapboxDraw.modes,
                draw_rectangle: DrawRectangle,
                draw_circle_two_click: circleTwoClickMode,
                draw_marker_circle: markerCircleMode,
            },
        });
        this.map.addControl(this.draw, 'top-left');
        this.drawExtras = new DrawExtraControls({ draw: this.draw });
        this.map.addControl(this.drawExtras, 'top-left');

        this.map.on('draw.create', this.handleDrawCreate);
        this.map.on('draw.update', this.handleDrawUpdate);
        this.map.on('draw.delete', this.handleDrawDelete);

        // Rede de segurança: os eventos marcam o fim de uma interação com as formas,
        // então é seguro devolver o arrasto ao mapa (ver restoreDragPan).
        ['draw.create', 'draw.update', 'draw.delete', 'draw.modechange'].forEach(
            (evento) => this.map.on(evento, this.restoreDragPan)
        );
    };

    // Recorta os empreendimentos carregados pelas formas desenhadas. Sem formas,
    // exibe tudo o que veio da busca por cidade/estado.
    applyShapeFilter = () => {
        if (!this.map?.getSource(SOURCE_ID)) return;

        const shapes = (this.draw?.getAll().features || []).filter(
            (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
        );

        let filtered = this.allFeatures;
        if (shapes.length > 0) {
            filtered = this.allFeatures.filter((point) =>
                shapes.some((shape) => {
                    try {
                        return booleanPointInPolygon(point, shape);
                    } catch {
                        return false;
                    }
                })
            );
        }

        const geojson = { type: 'FeatureCollection', features: filtered };
        this.currentGeojson = geojson;
        this.map.getSource(SOURCE_ID).setData(geojson);
        this.setState({ totalEmpreendimentos: filtered.length });
    };

    handleDrawCreate = (e) => {
        const feature = e.features?.[0];
        if (!feature || (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon')) return;

        const shapeType = feature.properties?.shapeType;
        const typeLabels = { circle: 'Círculo', marker_circle: 'Círculo', rectangle: 'Retângulo' };
        const type = typeLabels[shapeType] || 'Polígono';
        const idx = this.state.drawnShapes.length;
        const color = colors[idx % colors.length];

        const styleAttrs = { fillColor: color, fillOpacity: 0.3, strokeColor: '#000000', strokeWeight: 3 };
        Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(feature.id, k, v));

        const shape = {
            drawId: feature.id,
            type,
            name: `${type} ${idx + 1}`,
            color,
            visible: true,
            feature: this.draw.get(feature.id) || feature,
            center: feature.properties?.center || null,
            radiusMeters: feature.properties?.radiusMeters || null,
            ...styleAttrs,
        };

        this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, shape] }), this.applyShapeFilter);
    };

    handleDrawUpdate = (e) => {
        (e.features || []).forEach((feat) => {
            this.setState((prev) => ({
                drawnShapes: prev.drawnShapes.map((s) => s.drawId === feat.id
                    ? { ...s, feature: feat, center: feat.properties?.center || s.center, radiusMeters: feat.properties?.radiusMeters || s.radiusMeters }
                    : s),
            }));
        });
        this.applyShapeFilter();
    };

    handleDrawDelete = (e) => {
        // Esconder uma forma remove-a do draw; esses ids não devem sumir da lista.
        const deletedIds = (e.features || [])
            .map((f) => f.id)
            .filter((id) => {
                if (this._hidingIds.has(id)) { this._hidingIds.delete(id); return false; }
                return true;
            });
        if (deletedIds.length === 0) return;

        this.setState(
            (prev) => ({ drawnShapes: prev.drawnShapes.filter((s) => !deletedIds.includes(s.drawId)) }),
            this.applyShapeFilter
        );
    };

    // ===== Lista de formas (aba Ferramentas) =====

    updateShape = (drawId, patch) => this.setState((prev) => ({
        drawnShapes: prev.drawnShapes.map((s) => s.drawId === drawId ? { ...s, ...patch } : s),
    }));

    centerOnShape = (shape) => {
        const feat = shape.visible ? this.draw?.get(shape.drawId) : shape.feature;
        if (!feat?.geometry?.coordinates) return;
        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        walk(feat.geometry.coordinates);
        this.map.fitBounds(bounds, { padding: 60, duration: 600 });
    };

    toggleShapeVisibility = (shape) => {
        if (shape.visible) {
            const feat = this.draw?.get(shape.drawId) || shape.feature;
            this._hidingIds.add(shape.drawId);
            this.draw?.delete(shape.drawId);
            this.updateShape(shape.drawId, { visible: false, feature: feat });
        } else {
            const [id] = this.draw?.add(shape.feature) || [];
            this.updateShape(shape.drawId, { visible: true, drawId: id ?? shape.drawId });
        }
        this.applyShapeFilter();
    };

    setShapeRadius = (shape, radiusMeters) => {
        if (!shape.center || !(radiusMeters > 0)) return;
        const shapeType = shape.feature?.properties?.shapeType || 'circle';
        const updated = makeCircleFeature(shape.center, radiusMeters, shapeType);
        updated.properties = {
            ...updated.properties,
            fillColor: shape.fillColor, fillOpacity: shape.fillOpacity,
            strokeColor: shape.strokeColor, strokeWeight: shape.strokeWeight,
        };
        updated.id = shape.drawId;
        if (shape.visible) this.draw?.add(updated);
        this.updateShape(shape.drawId, { radiusMeters, feature: updated });
        this.applyShapeFilter();
    };

    editShapeAttributes = (shape) => this.setState({ shapeEditing: shape, shapeEditorOpen: true });
    // Ver restoreDragPan em MapboxMap/drawModes.js: sem isso o mapa perde o
    // clique-e-arrasto de vez quando um diálogo engole o mouseup de um arraste.
    restoreDragPan = () => restoreDragPan(this.map);

    closeShapeEditor = () => {
        this.restoreDragPan();
        this.setState({ shapeEditorOpen: false, shapeEditing: null });
    };

    applyShapeAttributes = (shape, attrs) => {
        if (!shape) return;
        const base = (shape.visible && this.draw?.get(shape.drawId)) || shape.feature;
        const updated = {
            ...base, id: shape.drawId,
            properties: {
                ...base.properties,
                fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
                strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            },
        };
        if (shape.type === 'Círculo' && attrs.radiusMeters > 0 && shape.center) {
            const st = updated.properties?.shapeType || 'circle';
            const geo = makeCircleFeature(shape.center, attrs.radiusMeters, st);
            updated.geometry = geo.geometry;
            updated.properties = { ...updated.properties, radiusMeters: attrs.radiusMeters };
        }
        if (shape.visible) {
            this.draw?.add(updated);
            // Antes do changeMode: o novo onSetup do simple_select congela o estado
            // atual do dragPan (ver restoreDragPan).
            this.restoreDragPan();
            this.draw?.changeMode('simple_select', { featureIds: [] });
        }
        const name = (attrs.name || '').trim() || shape.name;
        const cor = attrs.fillColor === 'transparent' ? attrs.strokeColor : attrs.fillColor;
        this.updateShape(shape.drawId, {
            name, color: cor, fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
            strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            radiusMeters: shape.type === 'Círculo' && attrs.radiusMeters > 0 ? attrs.radiusMeters : shape.radiusMeters,
            feature: updated,
        });
        this.closeShapeEditor();
        this.applyShapeFilter();
    };

    removeShape = (shape) => {
        if (shape.visible) this.draw?.delete(shape.drawId);
        this._hidingIds.delete(shape.drawId);
        this.setState(
            (prev) => ({ drawnShapes: prev.drawnShapes.filter((s) => s.drawId !== shape.drawId) }),
            this.applyShapeFilter
        );
    };

    thereAreDrawnShapes = () => (this.draw?.getAll().features || []).some(
        (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
    );

    // ===== Busca de endereço (Google Places) =====

    setupGooglePlaces = () => {
        loadGoogleMapsPlaces()
            .then(() => {
                if (!this.searchInputRef.current) return;

                const autocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current, {
                    fields: ['geometry', 'formatted_address', 'name'],
                    componentRestrictions: { country: 'br' },
                });
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry?.location) return;
                    const lng = place.geometry.location.lng();
                    const lat = place.geometry.location.lat();
                    this.map.flyTo({ center: [lng, lat], zoom: SELECTED_ZOOM, essential: true });
                    await this.loadFromAddress(lat, lng, [place]);
                });
                this.searchInputRef.current.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') e.preventDefault();
                });
            })
            .catch((err) => console.error('[MapaFornecedorMapbox] falha ao carregar Google Places:', err));
    };

    // Endereço buscado → resolve cidade/UF, sincroniza o formulário e carrega o mapa.
    loadFromAddress = async (lat, lng, place) => {
        let reverse;
        try {
            reverse = await geolocationService.reverse(lat, lng, place);
        } catch {
            toast.error('Não foi possível identificar a localidade.');
            return;
        }

        const uf = reverse?.uf;
        const cidade = reverse?.cidade;
        if (!uf || !cidade) {
            toast.warn('Localidade sem cidade/UF identificável.');
            return;
        }

        const cidadeOption = { value: `${uf}-${cidade}`, label: cidade };
        await this.handleCidadesChange([cidadeOption]);
        this.reloadEmpreendimentos();
    };

    // ===== Filtros =====

    // O `showClear` do MultiSelect devolve null; os campos de lista são normalizados
    // para [] para que os consumidores possam sempre iterar.
    handleFilterChange = (field, value) => {
        const listFields = ['bairros', 'material', 'incorporators'];
        const normalized = listFields.includes(field) ? (value || []) : value;

        this.setState((prev) => ({ filters: { ...prev.filters, [field]: normalized } }));
    };

    handleEstadoChange = async (uf) => {
        this.setState((prev) => ({ filters: { ...prev.filters, estado: uf } }));
        if (uf) await this.centralizarMapaNoEstado(uf);
    };

    // Centraliza no estado pelo geocoder externo. O mapa Google usava
    // route('state.capital'), que lê a coleção MongoDB CapitalCoordinates — o Mapbox
    // não pode criar dependência no Mongo (em desativação).
    centralizarMapaNoEstado = async (uf) => {
        if (!this.map) return;

        const estado = estados.find((e) => e.value === uf);
        if (!estado) return;

        try {
            const location = await geolocationService.location(`${estado.label} - Brasil`);
            if (location?.latitude && location?.longitude) {
                this.map.flyTo({ center: [location.longitude, location.latitude], zoom: 6 });
            }
        } catch (e) {
            console.error('[MapaFornecedorMapbox] erro ao centralizar no estado:', e);
        }
    };

    handleCidadesChange = async (cidades) => {
        // `estado` continua sendo só o do dropdown (restringe a busca de cidades e serve
        // de busca por UF quando nenhuma cidade foi escolhida). As UFs das cidades
        // selecionadas são derivadas em ufsSelecionadas().
        this.setState((prev) => ({ filters: { ...prev.filters, cidades } }));

        this.refreshBairrosAvailability(cidades);
        await this.loadBairrosOptions(cidades);
        if (this.state.showBairros) this.renderBairros(cidades);

        if (cidades.length === 0) return;

        try {
            const ultima = cidades[cidades.length - 1];
            const [ufCidade] = ultima.value.split('-');
            const location = await geolocationService.location(`${ultima.label} ${ufCidade} - Brasil`);
            if (location?.latitude && location?.longitude) {
                this.map?.flyTo({ center: [location.longitude, location.latitude], zoom: SELECTED_ZOOM });
            }
        } catch { /* posicionamento é best-effort */ }
    };

    // Opções do select de Bairro (MySQL — ver API\BairrosMapboxController).
    loadBairrosOptions = async (cidades) => {
        if (!cidades.length) {
            this.setState({ bairrosOptions: [] });
            return;
        }

        try {
            const response = await axios.post(route('api.bairros-fornecedor-mapbox'), {
                cidades: cidades.map((c) => c.value),
            });
            this.setState({ bairrosOptions: response.data || [] });
        } catch (e) {
            console.error('[MapaFornecedorMapbox] erro ao carregar bairros:', e);
            this.setState({ bairrosOptions: [] });
        }
    };

    cleanState = () => {
        (this.draw?.getAll().features || []).forEach((f) => this.draw.delete(f.id));
        this.allFeatures = [];
        this.currentGeojson = null;
        this.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
        this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        if (this.searchInputRef.current) this.searchInputRef.current.value = '';

        this.setState({
            filters: { ...INITIAL_FILTERS },
            bairrosOptions: [],
            drawnShapes: [],
            totalEmpreendimentos: 0,
            showBairros: false,
            bairrosAvailable: false,
            showSupplyTable: false,
        });
    };

    // ===== Marcadores =====

    // Paridade com o mapa Google: os marcadores respondem só a cidade/estado — os
    // demais campos do painel filtram a análise de insumos, no servidor.
    // `tipo`/`status` reproduzem o payload fixo de
    // empreendimentosServices.getEmpreendimentosWithoutMetaData ('Vertical' /
    // 'Comercialização', que no MySQL é o status 'Ativo').
    buildMarkersPayload = () => {
        const { cidades, estado } = this.state.filters;

        return {
            cidades: cidades.map((c) => c.value),
            // Busca por UF só quando nenhuma cidade foi escolhida — com cidade
            // selecionada o Estado serve apenas para filtrar o select de cidades
            // (mesma precedência do mapa Mapbox principal).
            estados: cidades.length === 0 && estado ? [estado] : [],
            tipo: ['Vertical'],
            status: ['Ativo'],
        };
    };

    // UFs efetivas da busca: as das cidades escolhidas ou, sem cidades, a do dropdown.
    ufsSelecionadas = () => {
        const { cidades, estado } = this.state.filters;

        if (cidades.length === 0) return estado ? [estado] : [];

        return [...new Set(cidades.map((c) => c.value.split('-')[0]))];
    };

    reloadEmpreendimentos = async () => {
        const { cidades, estado } = this.state.filters;

        if (cidades.length === 0 && !estado) {
            toast.warn('Selecione ao menos uma cidade ou um estado.');
            return;
        }

        this.setState({ loading: true });

        try {
            const response = await axios.post(
                route('api.empreendimentos-fornecedor-mapbox'),
                this.buildMarkersPayload()
            );

            this.allFeatures = response.data?.features || [];
            this.applyShapeFilter();

            if (this.allFeatures.length === 0) {
                toast.warn('Nenhum resultado com os parâmetros fornecidos.');
            } else {
                this.fitBoundsToLoadedFeatures();
            }
        } catch (e) {
            console.error('[MapaFornecedorMapbox] erro na busca de empreendimentos:', e);
            toast.error('Erro ao carregar empreendimentos.');
        } finally {
            this.setState({ loading: false });
        }
    };

    fitBoundsToLoadedFeatures = () => {
        const features = this.currentGeojson?.features || [];
        if (!features.length || !this.map) return;

        const bounds = new mapboxgl.LngLatBounds();
        features.forEach((f) => bounds.extend(f.geometry.coordinates));
        this.map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
    };

    // ===== Tabela de insumos =====

    // Ids (buildings.id) dos empreendimentos exibidos — recorte do relatório quando
    // há formas desenhadas.
    empreendimentosExibidos = () => [
        ...new Set((this.currentGeojson?.features || []).map((f) => f.properties?.id).filter(Boolean)),
    ];

    openSupplyFilter = () => {
        const { tipoAnalise, material, cidades, estado } = this.state.filters;

        if (!tipoAnalise) {
            toast.warn('Selecione o tipo de análise.');
            return;
        }

        if (cidades.length === 0 && !estado) {
            toast.warn('Selecione ao menos uma cidade ou um estado.');
            return;
        }

        // Mesma validação do mapa Google (FilterByType.validate).
        if (['Por fase de obra', 'Por Incorporadora'].includes(tipoAnalise) && material.length === 0) {
            toast.warn('É necessário ao menos um material.');
            return;
        }

        this.setState({ showSupplyTable: true });
    };

    closeSupplyFilter = () => {
        this.restoreDragPan();
        this.setState({ showSupplyTable: false });
    };

    // Payload dos relatórios de insumos no formato legado esperado por
    // TablePerPattern.preparePayload (reusado sem alteração): listas de {value,label}
    // e objeto único em pattern/construction_status.
    //
    // O Dropdown/MultiSelect guarda escalares no estado (sem `optionValue`, o PrimeReact
    // devolve `option.value`), então a conversão para o formato legado acontece aqui, na
    // fronteira. Cidades e incorporadoras já são objetos — o AutoComplete múltiplo guarda
    // a opção inteira.
    supplyFilters = () => {
        const f = this.state.filters;
        const asOption = (v) => ({ value: v, label: v });

        return {
            cities: f.cidades,
            // O relatório cruza cities × states, então as UFs precisam cobrir TODAS as
            // cidades escolhidas (podem ser de estados diferentes).
            states: this.ufsSelecionadas(),
            neighborhoods: f.bairros.map(asOption),
            status: ['Ativo'],
            supplies: f.material.map(asOption),
            buildings: [],
            construction_status: f.construction_status ? asOption(f.construction_status) : {},
            pattern: f.pattern ? asOption(f.pattern) : {},
            incorporators: f.incorporators,
            ticket_less_than: f.ticket_menor_que ?? '',
            ticket_greater_than: f.ticket_maior_que ?? '',
            area_less_than: f.area_menor_que ?? '',
            area_greater_than: f.area_maior_que ?? '',
            there_are_shapes: this.thereAreDrawnShapes(),
        };
    };

    // ===== Limites de bairros =====

    handleToggleBairros = (enabled) => {
        this.setState({ showBairros: enabled });
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        if (!enabled) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
            return;
        }

        const { cidades } = this.state.filters;
        if (cidades.length === 0) toast.info('Selecione uma cidade para ver os limites de bairros.');
        this.renderBairros(cidades);
    };

    renderBairros = async (cidades) => {
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;
        const groups = await Promise.all((cidades || []).map((c) => this.fetchBairrosGeojson(c.value)));
        this.map.getSource(BAIRROS_SOURCE_ID)?.setData({ type: 'FeatureCollection', features: groups.flat() });
    };

    fetchBairrosGeojson = async (value) => {
        if (this.bairrosCache[value]) return this.bairrosCache[value];

        const idx = value.indexOf('-');
        if (idx === -1) return [];
        const uf = value.slice(0, idx);
        const cidade = value.slice(idx + 1);

        try {
            const res = await axios.get(`/bairros/geojson/${encodeURIComponent(cidade)}/${encodeURIComponent(uf)}`);
            const features = res.data?.features || [];
            this.bairrosCache[value] = features;
            return features;
        } catch (e) {
            console.error('[MapaFornecedorMapbox] erro ao carregar limites de bairros:', e);
            return [];
        }
    };

    refreshBairrosAvailability = (cidades) => {
        const available = (cidades || []).some((c) => BAIRROS_CITIES.has(c.value));
        this.setState({ bairrosAvailable: available });
        if (!available && this.state.showBairros) {
            this.setState({ showBairros: false });
            this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        }
    };

    render() {
        const { left_side_bar_visible, active_tab, totalEmpreendimentos, filters } = this.state;

        return (
            <>
                <div ref={this.containerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />

                <input
                    ref={this.searchInputRef}
                    type="text"
                    placeholder="Digite um endereço"
                    className="fornecedor-mapbox-search"
                />

                {this.state.loading && (
                    <div className="mapbox-layer-loading" role="status">
                        <i className="fa fa-spin fa-spinner" /> Carregando empreendimentos…
                    </div>
                )}

                {/* Aleta lateral: a mesma abre e fecha a barra, acompanhando a borda dela
                    (substitui o "x" da Sidebar e o antigo botão de reabrir). Sai da frente
                    enquanto a tabela de insumos está aberta. */}
                {!this.state.showSupplyTable && (
                    <button
                        type="button"
                        className={`mapbox-flap mapbox-flap-left${left_side_bar_visible ? ' is-open' : ''}`}
                        title={left_side_bar_visible ? 'Ocultar barra lateral' : 'Mostrar barra lateral'}
                        aria-expanded={left_side_bar_visible}
                        onClick={() => this.setState({ left_side_bar_visible: !left_side_bar_visible })}
                    >
                        <i className={`fas ${left_side_bar_visible ? 'fa-angle-double-left' : 'fa-angle-double-right'}`} />
                    </button>
                )}

                <Sidebar
                    visible={left_side_bar_visible}
                    onHide={() => this.setState({ left_side_bar_visible: false })}
                    modal={false}
                    dismissable={false}
                    showCloseIcon={false}
                    className="mapbox-sidebar"
                >
                    <Tabs activeKey={active_tab} onSelect={(e) => this.setState({ active_tab: e })} id="fornecedor-mapbox-tabs">
                        <Tab eventKey="filtros" title="Filtros">
                            <div className="card-body" style={{ height: '80vh', overflowY: 'auto' }}>
                                <FormFiltersFornecedor
                                    filters={filters}
                                    bairrosOptions={this.state.bairrosOptions}
                                    loading={this.state.loading}
                                    onChange={this.handleFilterChange}
                                    onEstadoChange={this.handleEstadoChange}
                                    onCidadesChange={this.handleCidadesChange}
                                    onSubmit={this.reloadEmpreendimentos}
                                    onAnalisar={this.openSupplyFilter}
                                    onClear={this.cleanState}
                                />
                            </div>
                        </Tab>

                        <Tab eventKey="ferramentas" title="Ferramentas">
                            <div className="card-body" style={{ height: '80vh', overflowY: 'auto' }}>
                                <div className="form-group">
                                    <label className="mb-2 d-block">Exibir no mapa</label>
                                    <div className="mapbox-tools-switches">
                                        <div className="d-flex align-items-center" title="A preferência fica salva neste navegador.">
                                            <InputSwitch
                                                inputId="check_dark_mode"
                                                checked={this.state.mapStyle === 'dark'}
                                                onChange={(e) => this.setMapStyle(e.value ? 'dark' : 'light')}
                                            />
                                            <label htmlFor="check_dark_mode" className="pl-1 mb-0 ms-2">Modo noturno</label>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <InputSwitch
                                                inputId="check_empreendimentos"
                                                checked={this.state.showEmpreendimentos}
                                                onChange={(e) => this.toggleEmpreendimentos(e.value)}
                                            />
                                            <label htmlFor="check_empreendimentos" className="pl-1 mb-0 ms-2">Exibir empreendimentos</label>
                                        </div>
                                        {this.state.bairrosAvailable && (
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_bairros"
                                                    checked={this.state.showBairros}
                                                    onChange={(e) => this.handleToggleBairros(e.value)}
                                                />
                                                <label htmlFor="check_bairros" className="pl-1 mb-0 ms-2">Exibir limites de bairros</label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Empreendimentos no mapa</label>
                                    <span className="badge badge-secondary">{totalEmpreendimentos}</span>
                                    <small className="text-muted d-block mt-1">
                                        Ao desenhar áreas, a análise de insumos considera apenas os
                                        empreendimentos dentro delas.
                                    </small>
                                </div>

                                {this.state.drawnShapes.length > 0 && (
                                    <>
                                        <hr />
                                        <label className="mb-2 d-block">Formas desenhadas</label>
                                        <DrawnShapesList
                                            shapes={this.state.drawnShapes}
                                            onCenter={this.centerOnShape}
                                            onToggleVisibility={this.toggleShapeVisibility}
                                            onSetRadius={this.setShapeRadius}
                                            onEditAttributes={this.editShapeAttributes}
                                            onRemove={this.removeShape}
                                        />
                                    </>
                                )}
                            </div>
                        </Tab>
                    </Tabs>
                </Sidebar>

                <SupplyTableModal
                    visible={this.state.showSupplyTable}
                    type={{ value: filters.tipoAnalise, label: filters.tipoAnalise }}
                    filters={this.supplyFilters()}
                    buildings={this.empreendimentosExibidos()}
                    onClose={this.closeSupplyFilter}
                />

                <ShapeAttributesEditor
                    show={this.state.shapeEditorOpen}
                    shape={this.state.shapeEditing}
                    onHide={this.closeShapeEditor}
                    // O editor emite só o patch de atributos; a forma em edição vem
                    // do estado (mesma ligação do MapboxMap).
                    onApply={(attrs) => this.applyShapeAttributes(this.state.shapeEditing, attrs)}
                />

                <Toast ref={this.toastRef} position="top-right" />
            </>
        );
    }
}

export default MapaFornecedorMapbox;

if (document.getElementById('mapa-fornecedor-mapbox')) {
    ReactDOM.render(<MapaFornecedorMapbox />, document.getElementById('mapa-fornecedor-mapbox'));
}
